import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { Control, FieldPath, FieldValues } from "react-hook-form"
import { InputHTMLAttributes, TextareaHTMLAttributes } from "react"
import { cn } from "~/common/cn"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

type TextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  label: string
  labelClassName?: string
  control: Control<TFieldValues> | undefined
  name: TName
  type?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  multiline?: boolean
  rows?: number
} & (
  | (InputHTMLAttributes<HTMLInputElement> & { multiline?: false })
  | (TextareaHTMLAttributes<HTMLTextAreaElement> & { multiline?: true })
)

export const TextField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  labelClassName,
  type = "text",
  placeholder,
  disabled,
  required,
  multiline = false,
  rows,
  ...props
}: TextFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel
            className={cn("text-gray-400", fieldState.error && "text-destructive", labelClassName)}
          >
            {label}
            {required && <span className="text-destructive">*</span>}
          </FormLabel>
          <FormControl>
            {multiline ? (
              <Textarea
                {...field}
                placeholder={placeholder}
                disabled={disabled}
                rows={rows || 3}
                className="resize-none"
                {...(props as TextareaHTMLAttributes<HTMLTextAreaElement>)}
              />
            ) : (
              <Input
                {...field}
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                {...(props as InputHTMLAttributes<HTMLInputElement>)}
              />
            )}
          </FormControl>
          <FormMessage className="text-xs" />
        </FormItem>
      )}
    />
  )
}
